/**
 * Servicio para manejar conversiones de fechas sin timezone issues
 * 
 * Problema: JavaScript Date trabaja en UTC, lo que causa problemas de timezone
 * al convertir entre string (YYYY-MM-DD) y Date objects.
 * 
 * Solución: Este servicio proporciona métodos que trabajan exclusivamente con
 * zona horaria local, evitando conversiones UTC que causan desplazamientos de fechas.
 */
import { Injectable, Inject, LOCALE_ID } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateFormatterService {
  constructor(@Inject(LOCALE_ID) private localeId: string) {}

  /**
   * Convierte un string ISO (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss.sssZ) a un objeto Date local
   * 
   * @param dateString - String de fecha en formato ISO
   * @returns Date object creado en zona horaria local (sin UTC conversion)
   * 
   * @example
   * const date = stringToLocalDate("1988-08-08");
   * // date.getDate() === 8
   * // date.getMonth() === 7 (agosto es mes 7)
   * // date.getFullYear() === 1988
   */
  stringToLocalDate(dateString: string): Date | null {
    if (!dateString) return null;

    // Extraer solo la parte YYYY-MM-DD
    const dateStr = dateString.substring(0, 10); // "1988-08-08"

    // Dividir en componentes
    const [year, month, day] = dateStr.split('-').map(Number);

    // Validar componentes
    if (!year || !month || !day) {
      console.warn(`DateFormatterService: Formato de fecha inválido: ${dateString}`);
      return null;
    }

    // Crear Date usando componentes locales (no UTC)
    // new Date(year, month, day) crea en zona horaria local
    return new Date(year, month - 1, day);
  }

  /**
   * Convierte un objeto Date a un string en formato YYYY-MM-DD
   * 
   * @param date - Objeto Date a convertir
   * @returns String en formato YYYY-MM-DD
   * 
   * @example
   * const dateStr = localDateToString(new Date(1988, 7, 8));
   * // Retorna "1988-08-08"
   */
  localDateToString(date: Date | null): string | null {
    if (!date || !(date instanceof Date)) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Convierte un Date o string a string YYYY-MM-DD
   * Útil como normalizador para cualquier entrada de fecha
   * 
   * @param dateInput - Date object, string ISO, Luxon DateTime, o null
   * @returns String en formato YYYY-MM-DD o null
   */
  toISODateString(dateInput: Date | string | null | any): string | null {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
      return this.localDateToString(dateInput);
    }

    if (typeof dateInput === 'string') {
      // Si ya es string, extraer solo la parte de fecha
      return dateInput.substring(0, 10);
    }

    // Manejar objetos Luxon DateTime
    if (dateInput && typeof dateInput === 'object' && dateInput.toISO && typeof dateInput.toISO === 'function') {
      try {
        const iso = dateInput.toISO();
        // Luxon retorna formato ISO8601, extraer solo YYYY-MM-DD
        return iso.substring(0, 10);
      } catch (e) {
        console.warn('Error converting Luxon DateTime to ISO string:', e);
        return null;
      }
    }

    return null;
  }

  /**
   * Convierte un Date o string a un objeto Date local
   * Útil como normalizador para cualquier entrada de fecha
   * 
   * @param dateInput - Date object, string ISO, Luxon DateTime, o null
   * @returns Date object en zona horaria local, o null
   */
  toLocalDate(dateInput: Date | string | null | any): Date | null {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
      return dateInput;
    }

    if (typeof dateInput === 'string') {
      return this.stringToLocalDate(dateInput);
    }

    // Manejar objetos Luxon DateTime
    if (dateInput && typeof dateInput === 'object' && dateInput.toJSDate && typeof dateInput.toJSDate === 'function') {
      try {
        return dateInput.toJSDate();
      } catch (e) {
        console.warn('Error converting Luxon DateTime to JS Date:', e);
        return null;
      }
    }

    return null;
  }

  /**
   * Obtiene la fecha actual en formato YYYY-MM-DD
   * 
   * @returns String en formato YYYY-MM-DD de hoy
   */
  getTodayAsString(): string {
    return this.localDateToString(new Date()) || '';
  }

  /**
   * Obtiene la fecha actual como Date object local
   * 
   * @returns Date object de hoy en zona horaria local
   */
  getTodayAsDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  /**
   * Formatea una fecha como "ddd dd/MM/yyyy" según el locale configurado
   * Ejemplo: "lun 03/02/2026" (en es-AR)
   * 
   * @param date - Date object a formatear
   * @returns String formateado con día de semana, día, mes y año
   * 
   * @example
   * const formatted = formatDateWithDayName(new Date(2026, 1, 3));
   * // Retorna "lun 03/02/2026" (en locale es-AR)
   */
  formatDateWithDayName(date: Date | null): string {
    if (!date || !(date instanceof Date)) {
      return '';
    }

    return new Intl.DateTimeFormat(this.localeId, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }
}
